import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { createEquipment, updateEquipment } from '../../../services/equipment';
import { uploadImage } from '../../../services/upload';
import './EquipmentForm.css';

const EquipmentForm = ({ equipment, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    model: '',
    category: '',
    condition: 'excellent',
    quantity: '',
    price: '',
    description: '',
    image: '',
    available: true,
  });

  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (equipment) {
      setFormData(equipment);
      setImagePreview(equipment.image || '');
    }
  }, [equipment]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    const result = await uploadImage(file);

    if (result.success) {
      setFormData({ ...formData, image: result.url });
      setImagePreview(result.url);
    } else {
      alert('Upload failed: ' + (result.error || 'Unknown error'));
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview('');
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    if (!formData.quantity.trim()) newErrors.quantity = 'Quantity is required';
    if (!formData.price.trim()) newErrors.price = 'Price is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    const result = equipment
      ? await updateEquipment(equipment.id, formData)
      : await createEquipment(formData);

    setLoading(false);

    if (result.success) {
      onSave();
      onClose();
    } else {
      alert('Error saving equipment: ' + result.error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="equipment-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {equipment ? 'Edit Equipment' : 'Add New Equipment'}
          </h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="equipment-form">
          <div className="form-scroll">
            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              
              <div className="form-group">
                <label className="form-label">Equipment Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Sony A7S III"
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Sony"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Model</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="A7S III"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select Category</option>
                    <option value="Camera">Camera</option>
                    <option value="Lens">Lens</option>
                    <option value="Lighting">Lighting</option>
                    <option value="Audio">Audio</option>
                    <option value="Grip">Grip</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <span className="form-error">{errors.category}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="1"
                  />
                  {errors.quantity && <span className="form-error">{errors.quantity}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Price ($/day) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="150"
                  />
                  {errors.price && <span className="form-error">{errors.price}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      name="available"
                      checked={formData.available}
                      onChange={handleChange}
                      className="form-checkbox"
                    />
                    <span>Available for rent</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Describe the equipment..."
                />
              </div>
            </div>

            {/* Image */}
            <div className="form-section">
              <h3 className="form-section-title">Image</h3>
              
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="btn btn-ghost btn-sm"
                  >
                    Remove Image
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Upload Image</label>
                <div className="upload-container">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="upload-input"
                    disabled={uploading}
                  />
                  <label htmlFor="image-upload" className="upload-label">
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Choose Image to Upload'}
                  </label>
                </div>
                <p className="form-help">
                  Upload image (JPG, PNG, GIF, WebP - Max 5MB)
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Or Enter Image URL</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (equipment ? 'Update Equipment' : 'Add Equipment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentForm;